import { ESdChangeReason, IConditionalStyle, IConditionModel, IKuikaStyle } from "@kuika/kuika-cl-model";
import classNames from "classnames";
import { Guid } from "guid-typescript";
import _ from "lodash";
import React from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import { Comparison } from "../properties-panel/condition";
import {
  conditionalStyleDisplayName,
  conditionalStyleHasStyles,
  type IConditionalStyleNameValidation,
  moveConditionalStyle,
  validateConditionalStyleName
} from "./styling-window-helpers";

/**
 * KOA-9069: a mutation expressed as a function of the CURRENT list rather than a precomputed
 * array. Two mutations landing in one React batch would otherwise both read the pre-batch list
 * and the second would silently discard the first — a double-click on "Add Condition" losing one.
 * The parent applies this against a fresh read at write time, so the race cannot occur.
 */
export type ConditionalStylesUpdater = (current: IConditionalStyle[]) => IConditionalStyle[];

export interface IConditionalStylingComponentProps {
  /** Which conditional style the styling form is currently editing, if any. */
  selectedConditionalStyleId: string | null;
  onConditionalStylesChanged: (updater: ConditionalStylesUpdater, changeReason?: ESdChangeReason | undefined) => void;
  onSelectedConditionalStyleChanged: (conditionalStyleId: string | null) => void;
}

/**
 * KOA-9069 — the Conditional Styles section of the styling panel.
 *
 * Authoring reuses the shared `Comparison` builder unchanged, exactly as the visibility and
 * editability panels do: "condition ⇒ visual outcome" is already the house pattern and a
 * bespoke condition editor here would be a second grammar to keep in sync.
 *
 * Selecting an entry redirects the styling form at that entry (see `styling-window`), which is
 * why selection is owned by the parent rather than by local state — the form and this list must
 * never disagree about what is being edited.
 */
class ConditionalStylingComponent extends PureSdComponent<
  IConditionalStylingComponentProps & IWithSDProps,
  Record<string, never>
> {
  /**
   * Reads the DI directly rather than taking `styleObj` as a prop. `StylingWindow.checkNeedRender`
   * deliberately excludes `ChangeStyleValue` — it writes style values FROM the form, and
   * re-seeding the form on its own writes is the KOA-15838 bug family. So a prop from the parent
   * would never refresh after this section's own save. As a `withSD` context consumer this
   * component re-renders on the context change itself, gated by its own `checkNeedRender`.
   */
  private getStyleObj = (): IKuikaStyle | undefined => {
    const sd = this.props.sd.getScreenDesign();
    return this.props.sd.getSelectedDisplayItem(sd)?.styleObj;
  };

  private getConditionalStyles = (): IConditionalStyle[] => {
    return this.getStyleObj()?.conditionalStyles ?? [];
  };

  private handleAdd = (): void => {
    const added: IConditionalStyle = { id: Guid.create().toString() };

    this.props.onConditionalStylesChanged((current: IConditionalStyle[]): IConditionalStyle[] => [
      ..._.cloneDeep(current),
      added
    ]);
    // Open the new entry straight away: an unselected empty condition looks like nothing
    // happened, and the first thing the user wants is the condition builder.
    this.props.onSelectedConditionalStyleChanged(added.id);
  };

  private handleRemove = (conditionalStyleId: string): void => {
    // Removing the entry the form is bound to must also release the form, or the next edit
    // would be written against an id that no longer exists and silently vanish.
    if (this.props.selectedConditionalStyleId === conditionalStyleId) {
      this.props.onSelectedConditionalStyleChanged(null);
    }

    this.props.onConditionalStylesChanged((current: IConditionalStyle[]): IConditionalStyle[] =>
      _.cloneDeep(current).filter((entry: IConditionalStyle): boolean => entry.id !== conditionalStyleId)
    );
  };

  private handleMove = (conditionalStyleId: string, direction: "up" | "down"): void => {
    this.props.onConditionalStylesChanged((current: IConditionalStyle[]): IConditionalStyle[] =>
      _.cloneDeep(moveConditionalStyle(current, conditionalStyleId, direction))
    );
  };

  private handleSelect = (conditionalStyleId: string): void => {
    const isAlreadySelected: boolean = this.props.selectedConditionalStyleId === conditionalStyleId;
    this.props.onSelectedConditionalStyleChanged(isAlreadySelected ? null : conditionalStyleId);
  };

  /**
   * Writes the name on every keystroke rather than on blur, so the validation message tracks what
   * the user is typing. An invalid name is still persisted: rejecting the write would fight the
   * input and lose characters mid-word — the message is the feedback, and the display falls back
   * to "Condition {n}" wherever a name is unusable.
   */
  private handleNameChanged = (conditionalStyleId: string, name: string): void => {
    this.props.onConditionalStylesChanged((current: IConditionalStyle[]): IConditionalStyle[] => {
      const conditionalStyles: IConditionalStyle[] = _.cloneDeep(current);
      const target: IConditionalStyle | undefined = conditionalStyles.find(
        (entry: IConditionalStyle): boolean => entry.id === conditionalStyleId
      );
      if (target) target.name = name;
      return conditionalStyles;
    });
  };

  private handleConditionChanged = (
    conditionalStyleId: string,
    condition: IConditionModel,
    changeReason?: ESdChangeReason | undefined
  ): void => {
    this.props.onConditionalStylesChanged((current: IConditionalStyle[]): IConditionalStyle[] => {
      const conditionalStyles: IConditionalStyle[] = _.cloneDeep(current);
      const target: IConditionalStyle | undefined = conditionalStyles.find(
        (entry: IConditionalStyle): boolean => entry.id === conditionalStyleId
      );
      if (target) target.condition = condition;
      return conditionalStyles;
    }, changeReason);
  };

  private renderEntry = (entry: IConditionalStyle, index: number, all: IConditionalStyle[]): React.ReactNode => {
    const isSelected: boolean = this.props.selectedConditionalStyleId === entry.id;
    const isFirst: boolean = index === 0;
    const isLast: boolean = index === all.length - 1;
    const displayName: string = conditionalStyleDisplayName(entry, index);
    const hasStyles: boolean = conditionalStyleHasStyles(entry);
    const nameValidation: IConditionalStyleNameValidation = validateConditionalStyleName(
      entry.name ?? "",
      all,
      entry.id
    );
    // `name` stays undefined until the first keystroke, which is exactly "the user has not tried
    // yet" — so a freshly added condition shows its placeholder instead of a red "give it a name".
    // Clearing the field after typing leaves it defined-but-empty, and the error is right to show.
    const isNamePristine: boolean = entry.name === undefined;
    const showNameError: boolean = !isNamePristine && !nameValidation.isValid;

    return (
      <div
        key={entry.id}
        className={classNames("kuika-conditional-styling__entry", {
          "kuika-conditional-styling__entry--selected": isSelected,
          // A rule with a condition but no CSS does nothing at runtime. Without this it looks
          // identical to a working one, so the user has no way to spot the half-finished rule.
          "kuika-conditional-styling__entry--empty": !hasStyles
        })}
      >
        <div className="kuika-conditional-styling__entry-header">
          {/* The number IS the precedence — Condition 1 is the primary rule and wins a conflict,
              matching Excel and Sheets. The generator emits the CSS rules in reverse so the
              cascade agrees with this numbering. */}
          <span className="kuika-conditional-styling__entry-priority" aria-hidden="true">
            {index + 1}
          </span>
          <button
            type="button"
            className="kuika-conditional-styling__entry-title"
            onClick={() => this.handleSelect(entry.id)}
            aria-expanded={isSelected}
          >
            <KuikaSmallText>{displayName}</KuikaSmallText>
            {!hasStyles && <span className="kuika-conditional-styling__entry-hint">no style set</span>}
          </button>
          {/* Reordering is the only way to change precedence, so it lives here rather than behind
              a drag affordance. Buttons over drag: keyboard reachable, and a one-place move is
              the gesture people actually want. */}
          <button
            type="button"
            className="kuika-conditional-styling__entry-move"
            onClick={() => this.handleMove(entry.id, "up")}
            disabled={isFirst}
            aria-label={`Move ${displayName} up`}
            title="Move up (higher priority)"
          >
            <KuikaSvg name="arrow-up" type="arrows" width={12} height={12} />
          </button>
          <button
            type="button"
            className="kuika-conditional-styling__entry-move"
            onClick={() => this.handleMove(entry.id, "down")}
            disabled={isLast}
            aria-label={`Move ${displayName} down`}
            title="Move down (lower priority)"
          >
            <KuikaSvg name="arrow-down" type="arrows" width={12} height={12} />
          </button>
          <button
            type="button"
            className="kuika-conditional-styling__entry-remove"
            onClick={() => this.handleRemove(entry.id)}
            aria-label={`Remove ${displayName}`}
          >
            <KuikaSvg name="trash" width={14} height={14} />
          </button>
        </div>

        {isSelected && (
          <>
            <div className="kuika-conditional-styling__entry-name">
              <label className="kuika-conditional-styling__entry-name-label" htmlFor={`cond-name-${entry.id}`}>
                Name
              </label>
              <input
                id={`cond-name-${entry.id}`}
                type="text"
                className={classNames("kuika-conditional-styling__entry-name-input", {
                  "kuika-conditional-styling__entry-name-input--invalid": showNameError
                })}
                value={entry.name ?? ""}
                placeholder={`Condition ${index + 1}`}
                aria-invalid={showNameError}
                aria-describedby={showNameError ? `cond-name-err-${entry.id}` : undefined}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  this.handleNameChanged(entry.id, event.target.value)
                }
              />
              {showNameError && (
                <span className="kuika-conditional-styling__entry-name-error" id={`cond-name-err-${entry.id}`}>
                  {nameValidation.message}
                </span>
              )}
            </div>

            {/* entry-body is Comparison's slot. Nothing else goes in it — the name field above and
                the scope note below stay outside, so this renders exactly as it does for the
                Visibility and Editability panels. */}
            <div className="kuika-conditional-styling__entry-body">
              <Comparison
                conditionType="style"
                condition={entry.condition}
                onChange={(condition: IConditionModel, changeReason?: ESdChangeReason | undefined) =>
                  this.handleConditionChanged(entry.id, condition, changeReason)
                }
              />
            </div>

            <div className="kuika-conditional-styling__entry-scope">
              <KuikaSvg name="star" width={11} height={11} />
              <KuikaSmallText>The style sections above now edit this condition.</KuikaSmallText>
            </div>
          </>
        )}
      </div>
    );
  };

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return (
      changeReason === ESdChangeReason.ChangeStyleValue ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.ActionParameterFixedValueChanged
    );
  };

  render(): React.ReactNode {
    const conditionalStyles: IConditionalStyle[] = this.getConditionalStyles();

    return (
      <div className="kuika-conditional-styling">
        {conditionalStyles.length === 0 && (
          <div className="kuika-conditional-styling__empty">
            <KuikaSmallText>Style this by its data</KuikaSmallText>
          </div>
        )}

        {/* Precedence is invisible otherwise: the numbers alone do not say which way they run,
            and it is the first thing testers asked about. */}
        {conditionalStyles.length > 1 && (
          <div className="kuika-conditional-styling__precedence">
            <KuikaSvg name="info-circle" width={11} height={11} />
            <span>
              When several match, the <strong>topmost</strong> wins.
            </span>
          </div>
        )}

        {conditionalStyles.map(this.renderEntry)}

        <KuikaButton type="tertiary" size="sm" onClick={this.handleAdd}>
          Add Condition
        </KuikaButton>
      </div>
    );
  }
}

const conditionalStylingComponent = withSD<IConditionalStylingComponentProps>(ConditionalStylingComponent);
export default conditionalStylingComponent;
